// ** React Imports
import { useEffect, useState, useRef } from 'react';

// ** Redux Imports
import { useSelector } from 'react-redux';

// ** MUI Imports
import { useMediaQuery } from '@mui/material';

// ** Chart Imports
import Chart from 'react-apexcharts';

// ** Utils Imports
import { API } from '../../../../../../utility/API';
import { formatNumber } from '../../../../../helpers/formatNumber';

// ** Styles Imports
import styles from './styles.module.css';

function PortfolioPieChart() {
    // ** Media Queries
    const isMobileView = useMediaQuery('(max-width:768px)');

    // ** Redux State
    const hideBalance = useSelector(state => state.userData.value.hide_balance);
    const theme = useSelector(state => state.theme.value);
    const totalBalance = useSelector(state => state.fundsData?.data || {});

    // ** States
    const [loading, setLoading] = useState(true);
    const [pieData, setPieData] = useState([]);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [hoveredCoin, setHoveredCoin] = useState(null);
    const [chartReady, setChartReady] = useState(false);
    const [realtimePortfolioValue, setRealtimePortfolioValue] = useState(null);

    // ** WebSocket Ref
    const wsRef = useRef(null);

    // ** WebSocket Functions
    const connectWebSocket = () => {
        // Don't connect if already connected
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            return;
        }

        try {
            const wsUrl = window.location.host.includes('localhost')
                ? `ws://${window.location.host}/api/v2/ws/private`
                : `wss://${window.location.host}/api/v2/ws/private`;

            wsRef.current = new WebSocket(wsUrl);

            wsRef.current.onopen = () => {
                console.log('Portfolio PieChart WebSocket connected');

                // Subscribe to portfolio updates
                const subscribeMessage = {
                    event: 'subscribe',
                    streams: ['portfolio']
                };

                try {
                    wsRef.current.send(JSON.stringify(subscribeMessage));
                } catch (error) {
                    console.error('Error sending WebSocket subscription:', error);
                }
            };

            wsRef.current.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.portfolio && data.portfolio.portfolio_usd) {
                        // Update real-time portfolio value
                        setRealtimePortfolioValue(data.portfolio.portfolio_usd);
                    }
                } catch (error) {
                    console.error('Error parsing WebSocket message:', error);
                }
            };

            wsRef.current.onclose = (event) => {
                console.log('Portfolio PieChart WebSocket disconnected:', event.code, event.reason);

                // Only attempt to reconnect if it wasn't a manual close
                if (event.code !== 1000) {
                    setTimeout(() => {
                        if (wsRef.current?.readyState !== WebSocket.OPEN) {
                            connectWebSocket();
                        }
                    }, 5000);
                }
            };

            wsRef.current.onerror = (error) => {
                console.error('Portfolio PieChart WebSocket error:', error);
            };
        } catch (error) {
            console.error('Error creating WebSocket connection:', error);
        }
    };

    const disconnectWebSocket = () => {
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
    };

    // ** API Functions
    const fetchPieChartData = async () => {
        try {
            setLoading(true);
            const response = await API.get('/trading/account/portfolio/top_wallets');

            // Process API response
            const processedData = processApiData(response.data);
            setPieData(processedData);
        } catch (error) {
            console.error('Error fetching pie chart data:', error);
            // Fallback to demo data
            const demoData = [
                { name: 'Bitcoin', value: 45, color: '#f7931a' },
                { name: 'Ethereum', value: 25, color: '#627eea' },
                { name: 'Solana', value: 15, color: '#9945ff' },
                { name: 'USDT', value: 10, color: '#26a17b' },
                { name: 'Others', value: 5, color: '#8b9aab' }
            ];
            setPieData(demoData);
        } finally {
            setLoading(false);
        }
    };

    // ** Data Processing Functions
    const processApiData = (apiResponse) => {
        const { data } = apiResponse;

        // Fixed gradient colors - highest percentage to lowest percentage
        const GRADIENT_COLORS = [
            '#196969', // Highest percentage (darkest teal)
            '#193F3F', // Second highest
            '#0A9494', // Third highest
            '#91FFFF', // Fourth highest
            '#BCFFFF', // Fifth highest
            '#E5FFFF'  // Lowest percentage (lightest teal)
        ];

        // Convert to pie chart format using API-provided percentage
        const pieData = data.map(item => ({
            name: item.currency,
            value: item.percentage, // Use API-provided percentage directly
            usdValue: item.usd_value,
            balance: item.balance || null
        }));

        // Sort by percentage (largest first) and assign colors based on ranking
        const sortedData = pieData.sort((a, b) => b.value - a.value);

        // Assign colors based on percentage ranking
        const dataWithColors = sortedData.map((item, index) => ({
            ...item,
            color: GRADIENT_COLORS[index] || GRADIENT_COLORS[GRADIENT_COLORS.length - 1] // Use last color if more than 6 items
        }));

        return dataWithColors;
    };

    // ** Chart Configuration
    const chartHeight = isMobileView ? 300 : 400;
    const chartWidth = isMobileView ? 300 : 400;

    const chartOptions = {
        chart: {
            type: 'donut',
            background: 'transparent',
            toolbar: {
                show: false
            },
            animations: {
                enabled: false
            },
            // Disable events to prevent DOM access errors - using tooltip for hover info instead
            // events: {
            //     dataPointMouseEnter(event, chartContext, config) {
            //         try {
            //             if (config && 
            //                 typeof config.dataPointIndex === 'number' && 
            //                 config.dataPointIndex >= 0 && 
            //                 pieData && 
            //                 Array.isArray(pieData) && 
            //                 pieData[config.dataPointIndex]) {
            //                 setHoveredCoin(pieData[config.dataPointIndex]);
            //             }
            //         } catch (error) {
            //             console.warn('Chart hover enter error:', error);
            //         }
            //     },
            //     dataPointMouseLeave(event, chartContext, config) {
            //         try {
            //             setHoveredCoin(null);
            //         } catch (error) {
            //             console.warn('Chart hover leave error:', error);
            //         }
            //     }
            // }
        },
        theme: {
            mode: theme === 'light' ? 'light' : 'dark'
        },
        colors: pieData && Array.isArray(pieData) ? pieData.map(item => item.color || '#64748b') : [],
        labels: pieData && Array.isArray(pieData) ? pieData.map(item => item.name || 'Unknown') : [],
        plotOptions: {
            pie: {
                startAngle: -90,
                endAngle: 90,
                donut: {
                    size: '70%',
                    labels: {
                        show: true,
                        name: {
                            show: true,
                            fontSize: '18px',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                            fontWeight: 600,
                            color: theme === 'light' ? '#333' : '#fff',
                            offsetY: -10,
                            formatter: () => {
                                // Always show "Total"
                                return 'Total in USD';
                            }
                        },
                        value: {
                            show: true,
                            fontSize: '24px',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                            fontWeight: 700,
                            color: theme === 'light' ? '#333' : '#fff',
                            offsetY: 16,
                            formatter: () => {
                                if (hideBalance) return '••••••••';

                                // Use real-time value if available, otherwise fall back to Redux store
                                const totalValue = realtimePortfolioValue || totalBalance?.total_spot_balance || 0;
                                return `$${formatNumber(totalValue, 2)}`;
                            }
                        },
                        total: {
                            show: true,
                            showAlways: false, // Only show when not hovering
                            label: 'Total',
                            fontSize: '18px',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                            fontWeight: 600,
                            color: theme === 'light' ? '#333' : '#fff',
                            formatter: () => {
                                if (hideBalance) return '••••••••';
                                // Use real-time value if available, otherwise fall back to Redux store
                                const totalValue = realtimePortfolioValue || totalBalance?.total_spot_balance || 0;
                                return `$${formatNumber(totalValue, 2)}`;
                            }
                        }
                    }
                },
                expandOnClick: false,
                customScale: 1
            }
        },
        dataLabels: {
            enabled: false
        },
        tooltip: {
            enabled: true,
            theme: 'dark',
            followCursor: true,
            style: {
                fontSize: '14px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            },
            marker: {
                show: true,
                fillColors: ['#196969']
            },
            y: {
                formatter(val, opts) {
                    if (hideBalance) return '••••••••';
                    if (opts && opts.seriesIndex !== undefined && pieData && pieData[opts.seriesIndex]) {
                        const dataPoint = pieData[opts.seriesIndex];
                        return `$${dataPoint.usdValue.toLocaleString('en-US', { maximumFractionDigits: 0 })} (${val.toFixed(1)}%)`;
                    }
                    return `${val.toFixed(1)}%`;
                }
            }
        },
        legend: {
            show: false
        },
        responsive: [
            {
                breakpoint: 768,
                options: {
                    chart: {
                        width: 300,
                        height: 300
                    }
                }
            }
        ],
        stroke: {
            show: true,
            width: 1,
            colors: ['rgba(255, 255, 255, 0.2)']
        },
        states: {
            hover: {
                filter: {
                    type: 'lighten',
                    value: 0.3
                }
            },
            active: {
                allowMultipleDataPointsSelection: false,
                filter: {
                    type: 'darken',
                    value: 0.1
                }
            }
        }
    };

    const chartSeries = pieData && Array.isArray(pieData) ? pieData.map(item => item.value || 0) : [];

    // ** Effects
    useEffect(() => {
        fetchPieChartData();

        // Connect WebSocket for real-time updates
        connectWebSocket();

        // Cleanup WebSocket on unmount
        return () => {
            disconnectWebSocket();
        };
    }, []);

    // Set chart ready after data is loaded and component is mounted
    useEffect(() => {
        if (!loading && pieData && pieData.length > 0) {
            // Add a longer delay to ensure DOM is fully ready and prevent filter errors
            const timer = setTimeout(() => {
                setChartReady(true);
            }, 800);
            return () => clearTimeout(timer);
        } else {
            setChartReady(false);
        }
    }, [loading, pieData]);

    return (
        <div className={styles.pieChartContainer}>
            {/* Header Section */}
            <div className={styles.chartHeader}>
                <div className={styles.titleSection}>
                    <h3 className={styles.chartTitle}>Portfolio Allocation</h3>
                    <p className={styles.chartSubtitle}>
                        Distribution of your crypto assets
                    </p>
                </div>
            </div>

            {/* Chart Section */}
            <div className={styles.chartSection}>
                {loading ? (
                    <div className={styles.chartLoading}>
                        <div style={{
                            display: 'flex',
                            gap: '32px',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            justifyContent: 'center'
                        }}>
                            {/* Chart Loading */}
                            <div style={{ flex: '0 0 auto' }}>
                                <div className={styles.shimmerEffect} style={{
                                    width: `${chartWidth}px`,
                                    height: `${chartHeight}px`,
                                    borderRadius: '50%'
                                }}></div>
                            </div>
                            {/* Legend Loading */}
                            <div style={{
                                flex: '1',
                                minWidth: '200px',
                                maxWidth: '300px'
                            }}>
                                {[...Array(5)].map((_, index) => (
                                    <div key={index} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '12px 16px',
                                        marginBottom: '8px',
                                        background: 'rgba(255, 255, 255, 0.03)',
                                        borderRadius: '10px',
                                        border: '1px solid rgba(255, 255, 255, 0.05)'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                                            <div className={styles.shimmerEffect} style={{
                                                width: '12px',
                                                height: '12px',
                                                borderRadius: '3px'
                                            }} />
                                            <div style={{ flex: 1 }}>
                                                <div className={styles.shimmerEffect} style={{
                                                    width: '60px',
                                                    height: '14px',
                                                    marginBottom: '4px'
                                                }} />
                                                <div className={styles.shimmerEffect} style={{
                                                    width: '80px',
                                                    height: '12px'
                                                }} />
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div className={styles.shimmerEffect} style={{
                                                width: '40px',
                                                height: '14px',
                                                marginBottom: '4px'
                                            }} />
                                            <div className={styles.shimmerEffect} style={{
                                                width: '50px',
                                                height: '12px'
                                            }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : chartReady && pieData && pieData.length > 0 ? (
                    <div style={{
                        display: 'flex',
                        gap: '32px',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        justifyContent: 'center'
                    }}>
                        {/* ApexCharts Half Donut Chart */}
                        <div style={{ flex: '0 0 auto' }}>
                            {(() => {
                                try {
                                    return (
                                        <Chart
                                            options={chartOptions}
                                            series={chartSeries}
                                            type="donut"
                                            width={chartWidth}
                                            height={chartHeight}
                                        />
                                    );
                                } catch (error) {
                                    console.error('Chart rendering error:', error);
                                    return (
                                        <div style={{
                                            width: `${chartWidth}px`,
                                            height: `${chartHeight}px`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'var(--gray-color)',
                                            fontSize: '14px'
                                        }}>
                                            Chart unavailable
                                        </div>
                                    );
                                }
                            })()}
                        </div>

                        {/* Custom Legend */}
                        <div style={{
                            flex: '1',
                            minWidth: '200px',
                            maxWidth: '300px'
                        }}>
                            {pieData.map((item, index) => {
                                const formatCurrency = (value) => {
                                    return `$${value.toLocaleString('en-US', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}`;
                                };

                                return (
                                    <div
                                        key={index}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '12px 16px',
                                            marginBottom: '8px',
                                            background: 'rgba(255, 255, 255, 0.03)',
                                            borderRadius: '10px',
                                            border: '1px solid rgba(255, 255, 255, 0.05)',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                                            {/* Color indicator */}
                                            <div style={{
                                                width: '12px',
                                                height: '12px',
                                                borderRadius: '3px',
                                                background: item.color,
                                                flexShrink: 0
                                            }} />

                                            {/* Coin info */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: 'var(--text-color)',
                                                    marginBottom: '2px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {item.name}
                                                </div>
                                                <div style={{
                                                    fontSize: '12px',
                                                    color: 'var(--gray-color)',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {item.balance ? `${item.balance.toLocaleString('en-US', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 8
                                                    })} ${item.name}` : 'Portfolio allocation'}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Value and percentage */}
                                        <div style={{
                                            textAlign: 'right',
                                            marginLeft: '12px',
                                            flexShrink: 0
                                        }}>
                                            <div style={{
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#14b8a6',
                                                marginBottom: '2px'
                                            }}>
                                                {hideBalance ? '••••' : `${item.value.toFixed(1)}%`}
                                            </div>
                                            <div style={{
                                                fontSize: '12px',
                                                color: 'var(--gray-color)'
                                            }}>
                                                {hideBalance ? '••••••••' : formatCurrency(item.usdValue)}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: `${chartHeight}px`,
                        color: 'var(--gray-color)',
                        fontSize: '16px'
                    }}>
                        No portfolio data available
                    </div>
                )}
            </div>
        </div>
    );
}

export default PortfolioPieChart;
